import { minMax } from './arrayHelpers';
import { DepthData } from './depthDataHelpers';
import { Gwei } from './gwei';
import { PrettyMerge } from './tsUtilityTypes';

export const SPREAD_FRACTION_AT_MAX_ZOOM = 0.18; // Fraction of view taken up by spread at max zoom
export const DEFAULT_LIQUIDITY_PERCENTILE = 0.85; // Percentage of liquidity to show by default
export const BOUNDARY_PADDING = 0.05; // Padding added on either side of the default liquidity percentiles

// Domain type for recharts, simpler version of AxisDomain
export type Domain = [number, number];
export const domain = (min: number, max: number): Domain => [min, max];
export const getDomainRange = (domain: Domain): number => domain[1] - domain[0];

// Type for depth data with numeric values (for recharts)
type DepthDataNumeric = PrettyMerge<
  DepthData,
  {
    price: number;
    depth: number;
    totalCost: number;
    priceImpact: number;
  }
>;

// Convert data to numbers for recharts
export const transformDepthData = (item: DepthData): DepthDataNumeric => ({
  ...item,
  price: Gwei.fromGwei(item.price),
  depth: Gwei.fromGwei(item.depth),
  priceImpact: Gwei.fromGwei(item.priceImpact),
  totalCost: Gwei.fromGwei(item.totalCost),
});

// Find the price within which {percentile}% of the liquidity is in
export const getPriceAtPercentileDepth = (
  data: DepthDataNumeric[],
  percentile: number,
  side: 'bid' | 'ask',
  minPrice: number,
  maxPrice: number
): number => {
  // Ensure data is sorted
  const sortedData = data
    .filter((d) => d.depth > 0)
    .sort((a, b) => (side === 'bid' ? b.price - a.price : a.price - b.price));

  if (sortedData.length === 0) return side === 'bid' ? minPrice : maxPrice;

  // Since depth is cumulative, the max depth is the total liquidity
  const totalDepth = Math.max(...sortedData.map((d) => d.depth));
  const targetCumulativeDepth = totalDepth * percentile;

  // Find first item that meets target depth
  for (let i = 0; i < sortedData.length; i++) {
    const item = sortedData[i];
    if (item && item.depth >= targetCumulativeDepth) {
      return item.price;
    }
  }

  // Fallback to last item's price or boundary
  return sortedData[sortedData.length - 1]?.price || (side === 'bid' ? minPrice : maxPrice);
};

// Helper function to center a range on a price point with boundary constraints
export const centerRangeWithBoundaries = (
  range: number,
  centerPrice: number,
  minPrice: number,
  maxPrice: number
): Domain => {
  // Try to center on the given price
  let newMin = centerPrice - range / 2;
  let newMax = centerPrice + range / 2;

  // If we hit boundaries, adjust the other side to maintain requested range
  if (newMin < minPrice) {
    newMin = minPrice;
    newMax = Math.min(maxPrice, minPrice + range);
  } else if (newMax > maxPrice) {
    newMax = maxPrice;
    newMin = Math.max(minPrice, maxPrice - range);
  }

  return domain(newMin, newMax);
};

// Calculate a default zoom to show when the chart is loaded, which aims to show
// DEFAULT_LIQUIDITY_PERCENTILE % of the liquidity.
export const calculateDefaultZoomBoundaries = (
  bidData: DepthDataNumeric[],
  askData: DepthDataNumeric[],
  minPrice: number,
  maxPrice: number,
  midPrice: number,
  spread: number
): { defaultMinPrice: number; defaultMaxPrice: number } => {
  const defaultBidBoundary = getPriceAtPercentileDepth(
    bidData,
    DEFAULT_LIQUIDITY_PERCENTILE,
    'bid',
    minPrice,
    maxPrice
  );
  const defaultAskBoundary = getPriceAtPercentileDepth(
    askData,
    DEFAULT_LIQUIDITY_PERCENTILE,
    'ask',
    minPrice,
    maxPrice
  );

  // Use the larger range so that both percentile boundaries are included while
  // still being centered on midpoint, and add some padding so that the
  // boundaries are visible in the chart rather than being at the very edge.
  /**
   * @example
   * If 85% of bids are at midPrice - 10, 85% of asks at midPrice + 15 and
   * padding is 5%, then we'd take 15 plus 15*5% on either side, i.e. 31.5.
   */
  const bidSide = midPrice - defaultBidBoundary;
  const askSide = defaultAskBoundary - midPrice;
  const paddedRange = Math.max(bidSide, askSide) * 2 * (1 + BOUNDARY_PADDING);

  // Ensure we don't go tighter than max zoom allows
  const minAllowedRange = spread / SPREAD_FRACTION_AT_MAX_ZOOM;
  const finalRange = Math.max(paddedRange, minAllowedRange);

  // Center on midprice, constrained to data bounds
  const [defaultMinPrice, defaultMaxPrice] = centerRangeWithBoundaries(
    finalRange,
    midPrice,
    minPrice,
    maxPrice
  );

  return { defaultMinPrice, defaultMaxPrice };
};

// Helper function to create zero point for chart edge
export const createZeroPoint = (price: number, type: 'bid' | 'ask'): DepthDataNumeric => ({
  type,
  price,
  size: 0n,
  depth: 0,
  priceImpact: 0,
  totalCost: 0,
});

// Process depth data into values needed for depth chart
export const processDepthData = (data: DepthData[]) => {
  // Transform and split depth data
  const debtDataNumber = data.map(transformDepthData);
  const allBidData = debtDataNumber.filter((d) => d.type === 'bid');
  const allAskData = debtDataNumber.filter((d) => d.type === 'ask');

  // Calculate best bid/ask for the spread
  const bestBid = Math.max(...allBidData.map((d) => d.price));
  const bestAsk = Math.min(...allAskData.map((d) => d.price));

  // Add zero points at the spread so that the chart's lines are closed
  const bidData = [createZeroPoint(bestBid, 'bid'), ...allBidData];
  const askData = [createZeroPoint(bestAsk, 'ask'), ...allAskData];

  // Calculate price metrics
  const { min: minPrice, max: maxPrice } = minMax(
    [...allBidData, ...allAskData].filter((d) => d.size > 0n).map((d) => d.price)
  );
  const priceRange = maxPrice - minPrice;
  const spread = bestAsk - bestBid;
  const midPrice = (bestBid + bestAsk) / 2;

  // Calculate default zoom boundaries
  const { defaultMinPrice, defaultMaxPrice } = calculateDefaultZoomBoundaries(
    bidData,
    askData,
    minPrice,
    maxPrice,
    midPrice,
    spread
  );

  return {
    bidData,
    askData,
    bestBid,
    bestAsk,
    minPrice,
    maxPrice,
    priceRange,
    spread,
    midPrice,
    defaultMinPrice,
    defaultMaxPrice,
  };
};

// Calculate minimum zoom range based on spread
export const getMinZoomRange = (spread: number): number => spread / SPREAD_FRACTION_AT_MAX_ZOOM;

// Helper function to check if user is at maximum zoom out
export const isAtMaxZoomOut = (
  currentDomain: Domain,
  minPrice: number,
  maxPrice: number,
  tolerance = 0.001
): boolean => {
  const fullRange = maxPrice - minPrice;
  const currentRange = getDomainRange(currentDomain);
  return Math.abs(currentRange - fullRange) < tolerance;
};

// Check if current zoom is at maximum zoom in (tightest view)
export const isAtMaxZoomIn = (currentRange: number, spread: number, tolerance = 0.001): boolean => {
  const minZoomRange = getMinZoomRange(spread);
  return Math.abs(currentRange - minZoomRange) < tolerance;
};
